import type { HelpSection } from './helpContent.types'

export const programManagerHelpContent: HelpSection[] = [
  {
    title: 'Setup',
    subSections: [
      {
        entries: [
          {
            question: 'What Is the Setup Panel in the Sidekick Platform?',
            answer:
              'The Setup panel helps Program Managers configure their Sidekick channel so End Users can begin using the platform. It guides Program Managers through two key tasks: creating and publishing Content Modules and inviting End Users to the platform so they can begin interacting with Sidekicks.',
          },
          {
            question: 'When Is the Setup Process Considered Complete?',
            answer:
              'Setup is considered complete once at least one End User has been invited to the platform. After this step, the Setup checklist will no longer appear.',
          },
          {
            question: 'Where Can I Find the Setup Checklist in the Portal?',
            answer:
              'The Setup Checklist appears at the top of the Sidekick Portal screens when you first configure your channel and guides you through the required setup tasks. The set up checklist will disappear for all Program Managers once the first End User has been invited.',
          },
          {
            question: 'What Can Program Managers Do after Completing the Setup Process?',
            answer:
              'After setup is complete, Program Managers can continue creating content, inviting users, managing settings, managing billing, and monitoring usage through the Portal.',
          },
        ],
      },
    ],
  },
  {
    title: 'Content Library',
    subSections: [
      {
        entries: [
          {
            question: 'What Is the Content Library in the Sidekick Portal?',
            answer:
              'The Content Library contains all Content Modules created for your organization and allows Program Managers to manage, review, and publish content for End Users.',
          },
          {
            question: 'What Are the Different Tabs Available in the Content Library?',
            answer:
              'The Content Library is organized into several tabs that help Program Managers find and manage Content Modules at different stages of creation and publication:\n\n- All Modules \u2013 Displays every Content Module created in the channel by both Program Managers and Experts.\n- My Modules \u2013 Shows only the Content Modules that you created or that have been assigned to you.\n- Awaiting Approval \u2013 Lists Content Modules submitted by Experts that are waiting for review and approval from a Program Manager.\n- Published \u2013 Contains all Content Modules that have been approved and are currently available to End Users in the app.\n- In Progress \u2013 Includes Content Modules that are still being created, edited, or tested and are not yet published.\n- Help Modules \u2013 Provides preview access to Sidekick\u2019s built-in help Content Modules.\n- Module Prioritization \u2013 Allows Program Managers to arrange Content Modules in order of importance so Sidekicks know which Modules to recommend first to End Users.',
          },
          {
            question: 'What Are the Different Content Module Statuses and What Do They Mean?',
            answer:
              'Content Modules move through several statuses during the creation, review, and publishing process. Each status indicates where the module is in the workflow:\n\n- Draft \u2013 The Content Module is still being created or edited. It is not available to End Users or other Experts and can only be modified by its owner.\n- Testing \u2013 The Content Module has been sent to the mobile app for testing by its owner. Its owner interact with it in the app when the switch to End User - Test role, but is not yet visible to End Users.\n- Pending \u2013 The Content Module has been submitted by an Expert for review and approval by a Program Manager. It appears in the Awaiting Approval tab. The Module\u2019s owner and the Program Managers can interact with it in the app if they switch to the End User - Test role.\n- Published \u2013 The Content Module has been approved and is now available to all End Users in the Sidekick Platform app.',
          },
          {
            question: 'How Can a Program Manager Publish or Unpublish a Content Module?',
            answer:
              'Program Managers can change a module\u2019s status using the status dropdown in the Content Library. Under the status column, click publish, test, or move the module back to draft.',
          },
          {
            question: 'Can a Program Manager add owners to a Content Module?',
            answer:
              'Yes! Click the gear icon to the left of the Content Module. If needed, search for the Program Manager or Expert to add. Click the plus button next to the account to add. For Experts, you may enable Bypass Review (which allows them to publish Content Modules without your approval and edits Content Modules while they are published. When you are finished adding owners to the Content Module, click Save.',
          },
        ],
      },
    ],
  },
  {
    title: 'Content Module Creation',
    subSections: [
      {
        entries: [
          {
            question: 'How Do I Create a New Content Module in the Sidekick Platform?',
            answer:
              'From the Content Library page, click the New Module button and follow the steps to select a Sidekick, add file content, describe the Module, customize delivery, and save the module.',
          },
          {
            question: 'What Are the Different Ways to Add Content to a Content Module?',
            answer: 'Content can be added by uploading files, providing a URL, or manually entering text.',
          },
          {
            question: 'How Should Content Be Organized Across Sidekicks?',
            answer:
              'It is recommended that each Sidekick have one large Content Module that contains all materials related to a specific subject, rather than distributing similar information across multiple Sidekicks. This helps End Users get the most relevant information as quickly as possible.\n\nHowever, a Sidekick can have multiple Content Modules when they cover clearly distinct subject areas or are designed for different media types.',
          },
          {
            question: 'What File Types Are Supported for Content Module Uploads?',
            answer:
              'Supported file formats include images, videos, audio files, text files, PDFs, presentations, and documents such as PNG, JPG, MP4, WAV, PDF, PPTX, and DOCX.',
          },
          {
            question: 'Why would I want to add a video or YouTube URL?',
            answer:
              'When users are watching a video, they can ask questions about it. The Sidekicks can then answer questions, bring up additional information added in the Content Module. Sidekicks can even take the viewer to a specific part in a video, at either a specified time OR by asking "take me to the part where they discuss".',
          },
          {
            question: 'How do I add a YouTube URL?',
            answer:
              'To add a YouTube video, enter the URL in the Provide URL tab when adding a file. Then, when you review that file, enter the transcript for the YouTube in the transcript field. The title and description will generate automatically once the transcript is added.',
          },
          {
            question: 'Why Are FAQs Important in a Content Module?',
            answer:
              'FAQs help Sidekicks answer common questions quickly and guide End Users through the content more effectively.',
          },
          {
            question: 'What Makes a Good Content Module Title?',
            answer:
              'Use a clear, descriptive title that helps users immediately understand what the module is about. Avoid vague titles and focus on the topic or outcome the user will learn.\n\nExample:\n\nGood: *How to Apply for FAFSA*\n\nPoor: *Financial Help*',
          },
          {
            question: 'What Makes a Good Module Introduction?',
            answer:
              'A strong Module Introduction should:\n\n- Clearly explain what the module covers.\n- Be short (1\u20132 sentences).\n- End with a question that encourages the user to engage.\n\nExample:\n\n"This module explains how to complete your FAFSA application step by step. Would you like help getting started?"',
          },
          {
            question: 'How Much Content Should I Include in a Module?',
            answer:
              'Include enough content to fully explain the topic. Multiple files can be added to provide supporting information, videos, or documents that the Sidekick can reference when needed.',
          },
          {
            question: 'How Can I Improve the Quality of AI-Generated Suggestions?',
            answer:
              'Click the arrow next to the "Ask Sidekick" button. Choose the Guide option. Provide detailed instructions for the AI so it can alter the output to suit your needs.',
          },
          {
            question: 'Can people collaborate to create a Content Module?',
            answer:
              'Yes! You can add multiple owners to a Content Module and they can edit it one at a time. Click the gear icon to the left of the Content Module. If needed, search for the Program Manager or Expert to add. Click the plus button next to the account to add. When you are finished adding owners to the Content Module, click Save.',
          },
        ],
      },
    ],
  },
  {
    title: 'Testing and Publication',
    subSections: [
      {
        entries: [
          {
            question: 'How Do Program Managers Find Content Modules That Are Ready for Review?',
            answer:
              'Modules submitted by Experts appear in the Awaiting Approval tab with a Pending status. They also show in the All Modules tab with the Pending status.',
          },
          {
            question: 'Why Should Content Modules Be Tested in the App Before Publishing?',
            answer:
              'Testing allows Program Managers to experience the content as an End User would and confirm that the Module behaves correctly. If the Program Manager is reviewing an Expert\u2019s Content Module that is in Pending status, the Program Manager will be able to interact with that Module in the app if they switch to the End User - Test role.',
          },
          {
            question: 'How Do I Test Content Modules in the Mobile App?',
            answer:
              'Program Managers can test Content Modules in the mobile app to experience the content the same way End Users will.\n\nFor your own Content Modules:\n\n- In the Content Library, change the module status to Test in App.\n- This sends the module to the mobile app so only you can interact with it.\n\nFor Content Modules submitted by Experts:\n\n- If the module is in Pending status, you do not need to change the status.\n- Pending modules are automatically available for you to test.\n\nTo test in the mobile app:\n\n- Open the Sidekick mobile app.\n- Tap the menu in the lower right corner.\n- Select Change Role.\n- Choose End User \u2013 Test.',
          },
          {
            question: 'What Is the Difference Between the End User \u2013 Test Role and the End User Role?',
            answer:
              'The End User \u2013 Test role allows access to testing and pending Modules, while the End User role only displays published Modules.',
          },
          {
            question: 'How Do Program Managers Publish a Content Module for End Users?',
            answer:
              'In the Content Library, find the Module and selected Publish in the status column, making it immediately available to End Users.',
          },
        ],
      },
    ],
  },
  {
    title: 'People',
    subSections: [
      {
        entries: [
          {
            question: 'What Is the People Page in the Sidekick Platform?',
            answer:
              'The People page allows Program Managers to manage End Users, Experts, and Program Managers within their channel.',
          },
          {
            question: 'What do the different statuses mean?',
            answer:
              '- Invited: user has been invited to the channel, but has not accessed it\n- Active: user who has accepted the invitation and logged into the Sidekick Platform Portal. They have not interacted with a Sidekick in the app yet. Active users count toward billing\n- Subscribed: user has accepted the invitation, accessed the channel, and sent at least one message to the Sidekicks\n- Invitation Expired: user was invited, but they did not accept the invitation before it expired',
          },
          {
            question: 'Can I Invite Another Program Manager?',
            answer:
              'Yes. Program Managers can invite additional Program Managers to help manage the Sidekick Platform. Program Managers have full administrative permissions, including configuring settings, creating and approving Content Modules, and managing users.\n\nTo invite a Program Manager:\n\n- Go to the People page in the Sidekick Portal.\n- Select the Program Managers tab.\n- Click Invite Program Manager.\n- Enter the person\u2019s email address and optionally their name.\n- Click Invite Program Manager to send the invitation.\n\nOnce they accept the invitation and log in, they will have access to manage the channel through the Portal. They will also be able to log into the app and get help for Program Managers or change roles and experience the app as an End User.',
          },
          {
            question: 'Can I Invite an Expert to Create Content?',
            answer:
              'Yes. Program Managers can invite Experts to help create Content Modules for their channel. Experts can access the Sidekick Portal and build content that Expert Sidekicks use to support End Users.\n\nTo invite an Expert:\n\n- Go to the People page in the Sidekick Portal.\n- Select the Experts tab.\n- Click Invite Expert.\n- Enter the Expert\u2019s email address and optionally their name.\n- Optionally include a personal message in the invitation email.\n- Click Invite Expert to send the invitation.\n\nAfter accepting the invitation, the Expert can log into the Portal and begin creating Content Modules. They will also be able to log into the app and get help for Program Managers or change roles and experience the app as an End User.',
          },
          {
            question: 'What Is the Difference Between an Expert and a Program Manager?',
            answer:
              'Experts and Program Managers have different roles within the Sidekick Platform.\n\nProgram Managers are administrators who configure the platform, manage users, create and approve Content Modules, and control what content is published for End Users.\n\nExperts are content creators who contribute knowledge by building Content Modules that Sidekicks use to support End Users.',
          },
          {
            question: 'How Do I Invite a New End User to the Platform?',
            answer: 'Go to the People screen, select the End Users tab, and click Invite End User.',
          },
          {
            question: 'What Do the Different User Account Statuses Mean?',
            answer:
              'User account statuses indicate where a user is in the onboarding and usage process within the Sidekick Platform.\n\n- Invited \u2013 The user has been sent an invitation email to join the platform within the last 30 days but has not yet logged in. Invited users count toward billing.\n- Active \u2013 The user has accepted the invitation and logged into the platform, but may not have interacted with a Sidekick yet. This applies only to Experts.\n- Subscribed \u2013 The user has logged in and sent at least one message to a Sidekick, meaning they are actively using the platform.\n- Invitation Expired \u2013 The user was invited more than 30 days ago but never logged in. The invitation is no longer valid and the user will need a new invitation to access the platform.',
          },
          {
            question: 'How Can a Program Manager Remove an End User From the Platform?',
            answer: 'Find the user in the table, click the three-dot menu, and select Remove.',
          },
        ],
      },
    ],
  },
  {
    title: 'Feedback',
    subSections: [
      {
        entries: [
          {
            question: 'What Is the Feedback Page in the Sidekick Platform?',
            answer: 'The Feedback page shows feedback submitted by End Users about Content Modules.',
          },
          {
            question: 'What Types of Feedback Can Be Viewed on the Feedback Page?',
            answer:
              'Feedback includes ratings such as thumbs up or thumbs down as well as written comments from End Users.',
          },
          {
            question: 'What Information Is Shown in the Feedback Table?',
            answer:
              'The table displays the date, topic, module, rating, tags, comments, and additional details about the feedback.',
          },
          {
            question: 'How Can Program Managers View Detailed Feedback Submissions?',
            answer:
              'Click the detail icon to open the Feedback Details panel, which shows the user comment, the Sidekick response they reacted to, and the surrounding conversation history.',
          },
          {
            question: 'Does Feedback Remain Available if a Module or Topic Is Removed?',
            answer: 'Yes. Feedback entries remain in the system even if the associated Module or topic is deleted.',
          },
          {
            question: 'Who Can View Feedback on the Feedback Page?',
            answer:
              'Program Managers and Experts can view feedback in the Sidekick Portal. Experts can typically see feedback related to the Content Modules they own, while Program Managers can view feedback across all Content Modules in their channel.',
          },
          {
            question: 'What Do the Summary Metrics at the Top of the Feedback Page Show?',
            answer:
              'The summary metrics provide a high-level overview of feedback activity across the platform. These include:\n\n- **Modules** \u2013 The number of Content Modules that have received feedback\n- **Comments** \u2013 The total number of written feedback comments from End Users\n- **Thumbs Up** \u2013 The total number of positive feedback submissions\n- **Thumbs Down** \u2013 The total number of negative feedback submissions',
          },
          {
            question: 'What Do the Feedback Ratings Mean?',
            answer:
              'Feedback ratings indicate the End User\u2019s sentiment about the response they received:\n\n- **Thumbs Up** \u2013 Positive feedback\n- **Thumbs Down** \u2013 Negative feedback\n- **Question Mark (?)** \u2013 No rating was provided',
          },
          {
            question: 'What Does the Conversation History Show in the Feedback Details Panel?',
            answer:
              'The conversation history shows the interaction between the End User and the Sidekick leading up to the feedback submission. Messages are displayed in chronological order and labeled by speaker, providing full context for why the feedback was given.',
          },
          {
            question: 'Can I Filter or Sort Feedback Results?',
            answer:
              'Yes. The Feedback table allows you to sort and filter results to better analyze feedback. You can:\n\n- Sort by columns such as date, module, rating, or tag\n- Apply filters to narrow results\n- Show or hide columns\n- Adjust table density or view the table in fullscreen\n\nThese tools help Program Managers and Experts quickly find relevant feedback and identify trends.',
          },
        ],
      },
    ],
  },
  {
    title: 'Metrics',
    subSections: [
      {
        entries: [
          {
            question: 'Who Can Access the Metrics Page?',
            answer:
              'Experts and Program Managers can view metrics, but Program Managers have access to additional Expert activity metrics.',
          },
          {
            question: 'What Does the Expert Metrics Tab Show?',
            answer: 'It shows how often Experts visit the Portal and how frequently they edit Content Modules.',
          },
          {
            question: 'What Is the Metrics Page Used For?',
            answer:
              'The Metrics page shows how End Users are interacting with the Sidekick Platform. It provides anonymized statistics about system usage, including conversations, messages, and how often topics or Content Modules are used.',
          },
          {
            question: 'What Tabs Are Available on the Metrics Page?',
            answer:
              'The Metrics page includes several tabs:\n\n- Overview \u2013 Overall platform usage statistics.\n- Topics \u2013 Usage metrics for specific topics (if topics are enabled).\n- Content Modules \u2013 Usage metrics for individual Modules.',
          },
          {
            question: 'Is the Data on the Metrics Page Real-Time?',
            answer: 'No. Metrics data is updated several times each day, but it is not displayed in real time.',
          },
          {
            question: 'Does the Metrics Page Show Information About Individual Users?',
            answer:
              'No. All metrics are aggregated and anonymized, so the page shows overall usage trends rather than information about individual End Users.',
          },
          {
            question: 'What Information Does the Overview Tab Show?',
            answer:
              'The Overview tab provides a snapshot of overall platform activity, including:\n\n- Total conversations with Sidekicks.\n- A conversation is all the messages sent between a user and a specific Sidekick until either the user moves to a different Sidekick OR 48 hours have elapsed with no user activity.\n- Total messages sent by End Users.\n- Average daily messages.\n- Unique conversations.\n- Number of modules users interacted with.\n\nIt also includes charts showing daily active users, messages sent, and Content Module conversations over time.',
          },
          {
            question: 'What Metrics Are Available for Content Modules?',
            answer:
              'In the Metrics, Content Modules tab shows usage metrics for a selected item, including:\n\n- Unique visitors\n- Total conversations\n- Messages sent by End Users\n- Average conversation length\n- Longest conversation\n\nThese pages also include charts showing how usage changes over time.',
          },
        ],
      },
    ],
  },
  {
    title: 'Billing',
    subSections: [
      {
        entries: [
          {
            question: 'What Is the Billing and Account Management Page in the Sidekick Platform?',
            answer:
              'This page allows administrators to view pricing tiers, billing details, payment methods, and user usage.',
          },
          {
            question: 'How Does the Sidekick Platform Pricing Model Work?',
            answer:
              'Pricing is based on the number of users in your channel. The system automatically places your organization into the appropriate pricing tier based on the total number of users on your billing date. As your number of users increases, the per-user price decreases according to the tier you qualify for.\n\nThe pricing tiers are:\n\n- 10 or fewer users \u2013 Free\n\n  Your channel can have up to 10 users at no cost. This acts as a free trial and does not require a payment method.\n- 11\u2013100 users \u2013 $5 per user per month\n\n  Once you invite your 11th user, billing begins and you will be charged $5 per user each month for the total number of users in this range.\n- 101\u20131,000 users \u2013 $4 per user per month\n\n  When your organization reaches 101 users, the per-user price automatically drops to $4 per user per month.\n- 1,001+ users \u2013 $3 per user per month\n\n  For organizations with more than 1,000 users, the price automatically decreases to $3 per user per month.\n\nYou do not need to manually select a pricing plan. The platform automatically applies the correct pricing tier based on the number of users in your channel on your monthly billing date.',
          },
          {
            question: 'Who Counts as a User for Billing Purposes?',
            answer:
              'Users are counted for billing based on their account status in the system. The following statuses count toward your total number of users:\n\n- Invited \u2013 A user who has been sent an invitation email but has not yet logged in. Invited users count toward billing for up to 30 days after the invitation is sent. If the user does not log in within those 30 days, the invitation expires and the user will no longer count as a seat for billing purposes.\n- Active \u2013 A user who has accepted the invitation and logged into the Sidekick Platform Portal. They have not interacted with a Sidekick in the app yet. Active users count toward billing.\n- Subscribed \u2013 A user who has logged in and sent at least one message to a Sidekick. This indicates they are actively using the platform. Subscribed users count toward billing.\n\nIf a user\u2019s invitation expires after 30 days without being accepted, they will move to Invitation Expired status and will not count toward billing unless they are invited again.',
          },
          {
            question: 'When Are Billing Charges Applied to the Account?',
            answer: 'Charges are applied monthly based on the number of users in the channel on the billing date.',
          },
          {
            question: 'How Do Program Managers Add or Update a Payment Method?',
            answer: 'Go to the Billing page, open the Payment tab, and add or update the credit card information.',
          },
        ],
      },
    ],
  },
  {
    title: 'Settings',
    subSections: [
      {
        entries: [
          {
            question: 'What Is the Welcome Message Tab?',
            answer:
              'The Welcome Message tab allows Program Managers to edit the message End Users receive the first time they log in to the Sidekick Platform mobile app. This message is delivered by the user\u2019s Personal Sidekick and typically introduces the platform and explains how the Sidekick can help them get started.\n\nTo update the message, type or edit the text in the message box and click Save. It is recommended to keep the message short, explain what the platform offers, and end with a question to encourage the user to begin interacting with the Sidekick.',
          },
          {
            question: 'What Is the Personal Sidekick Tab?',
            answer:
              'The Personal Sidekick tab allows Program Managers to choose which Personal Sidekicks End Users can select in the mobile app. Personal Sidekicks guide users through the platform and help them find relevant content.\n\nOn this tab, you can select which Sidekicks are available to users. You must select at least 4 and up to 6 Personal Sidekicks. You can also choose one Sidekick as the default, which will automatically be assigned if the End User does not choose one. After making changes, click Save to apply them.',
          },
          {
            question: 'What Is the User Profile Tab?',
            answer:
              'The User Profile tab allows Program Managers to choose which profile fields End Users will complete in the mobile app. These fields provide information that helps Sidekicks personalize recommendations and guidance for each user.\n\nSelect or deselect the fields you want to include. Only the fields that are checked will be shown in the mobile app. You can also drag fields to change their order in a section and drag section to change their order as they display in the app.',
          },
          {
            question: 'What Is the Branding Tab?',
            answer:
              'The Branding tab allows Program Managers to personalize the app experience for End Users. From this tab, you can add your organization\u2019s logo and create branded banner and splash screen assets so the app reflects your organization\u2019s identity.\n\nYou can also customize the terms the Sidekicks will use to refer to your channel and to your End Users. This helps align the platform with your organization\u2019s language.\n\nOnce updated, click Save and the splash screen, branded banner, and terms will immediately be used by Sidekicks in the app.',
          },
          {
            question: 'What Is the Topics Tab?',
            answer:
              'The Topics tab allows Program Managers to create and manage Topics, which organize Content Modules. Topics help Sidekicks recommend the most relevant content to End Users.\n\nFrom this tab, you can add new topics, edit existing ones, duplicate topics, or remove topics that are no longer needed. Click Save after making changes to apply them.',
          },
          {
            question: 'What Is the General Information Tab?',
            answer:
              'The General Information tab allows Program Managers to add organization-wide content that is available to all Sidekicks. This can include information such as your organization\u2019s mission, history, or other key details, helping ensure Sidekicks provide consistent and accurate context to End Users.',
          },
          {
            question: 'Which Sidekicks have access to the General Information content?',
            answer: 'All Sidekicks in your channel have access to the content from the General Information tab.',
          },
        ],
      },
    ],
  },
  {
    title: 'Channel Switching and Logout',
    subSections: [
      {
        entries: [
          {
            question: 'What Does Switching Channels Mean in the Sidekick Platform?',
            answer:
              'Switching channels allows users who have access to multiple organizations to move between different Sidekick environments.',
          },
          {
            question: 'I Have Been Invited to Multiple Channels. Can I Switch Between Them?',
            answer:
              'Yes. If your account has access to multiple channels, you can switch between them within the Sidekick Portal. Switching channels allows you to view and manage the content, users, and settings for each organization you have access to.\n\nTo switch channels, click your profile icon in the top right corner in the Portal and choose the channel you want to access. The Portal will then update to display the data, settings, and content associated with that specific channel.',
          },
          {
            question: 'How Do I Switch Between Channels in the Sidekick Portal?',
            answer:
              'Click your profile icon in the top right corner in the Portal to choose another channel available to your account.',
          },
          {
            question: 'How Do I Switch Channels in the App While I Am Testing?',
            answer:
              'If your account has access to multiple channels, you can switch between them directly in the mobile app while testing.\n\nTo switch channels in the app:\n\n- Tap the three-dot menu in the lower right corner of the app.\n- Select Change Channel.\n- Choose the channel you want to switch to from the list.\n- The app will refresh and load the content, Sidekicks, and modules associated with that channel.\n\nThis allows you to test and experience the app exactly as End Users would within each channel you have access to.',
          },
          {
            question: 'How Do I Log Out of the Sidekick Portal?',
            answer: 'Click your profile icon in the top right corner in the Portal and select Log Out.',
          },
          {
            question: 'Will Logging Out Affect My Saved Work in the Portal?',
            answer: 'No. Any saved changes remain stored in the system.',
          },
          {
            question: 'What Should I Do if I Cannot Access My Account?',
            answer:
              'Contact your organization\u2019s administrator or Sidekick support for assistance. [support@sidekickplatform.com](mailto:support@sidekickplatform.com)',
          },
        ],
      },
    ],
  },
]
